/**
 * Component Mutations Hook
 * Handles updating, deleting, and reordering checklist components
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { ChecklistComponent } from "@/types/components";

const apiUrl = import.meta.env.VITE_CLOUDFLARE_API_URL || "http://localhost:8788";

interface UpdateComponentInput {
  id: number;
  data: Partial<ChecklistComponent>;
}

interface ReorderComponentsInput {
  checklistId: number;
  componentIds: number[];
}

export const useComponentMutations = (checklistId?: number) => {
  const queryClient = useQueryClient();

  const updateComponentMutation = useMutation({
    mutationFn: async (input: UpdateComponentInput) => {
      const response = await fetch(`${apiUrl}/api/v1/checklists/components/${input.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input.data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to update component");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["checklist-components", checklistId] });
      toast.success("Component updated successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to update component: ${error.message}`);
    },
  });

  const deleteComponentMutation = useMutation({
    mutationFn: async (componentId: number) => {
      const response = await fetch(`${apiUrl}/api/v1/checklists/components/${componentId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to delete component");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["checklist-components", checklistId] });
      toast.success("Component deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete component: ${error.message}`);
    },
  });

  const reorderComponentsMutation = useMutation({
    mutationFn: async (input: ReorderComponentsInput) => {
      const response = await fetch(
        `${apiUrl}/api/v1/checklists/${input.checklistId}/components/reorder`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            component_ids: input.componentIds,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to reorder components");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["checklist-components", checklistId] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to reorder components: ${error.message}`);
    },
  });

  return {
    updateComponentMutation,
    deleteComponentMutation,
    reorderComponentsMutation,
    isUpdating: updateComponentMutation.isPending,
    isDeleting: deleteComponentMutation.isPending,
    isReordering: reorderComponentsMutation.isPending,
  };
};
